import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './PolitiqueConfidentialite.module.css';

const SECTION_IDS = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9', 's10', 's11'];

const TOC_ITEMS = [
  { id: 's1', label: '1. Introduction' },
  { id: 's2', label: '2. Responsable' },
  { id: 's3', label: '3. Données collectées' },
  { id: 's4', label: '4. Finalités' },
  { id: 's5', label: '5. Base légale' },
  { id: 's6', label: '6. Partage des données' },
  { id: 's7', label: '7. Conservation' },
  { id: 's8', label: '8. Vos droits' },
  { id: 's9', label: '9. Sécurité' },
  { id: 's10', label: '10. Transferts hors UE' },
  { id: 's11', label: '11. Modifications' },
];

function PolitiqueConfidentialite() {
  const [activeId, setActiveId] = useState('s1');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.pageRoot}>
      <header className={styles.legalHeader}>
        <div className={styles.headerInner}>
          <div className={styles.headerIntro}>
            <Link to="/" className={styles.backLink}>
              ← Retour à l’accueil
            </Link>
            <div className={styles.brandBadge}>
              <div className={styles.brandDot} />
              <span>Dy'kass — Document légal</span>
            </div>
          </div>
          <h1 className={styles.title}>
            Politique de
            <br />
            <em>Confidentialité</em>
          </h1>
          <p className={styles.headerSub}>Conformément au Règlement (UE) 2016/679 — RGPD</p>
          <div className={styles.headerMeta}>
            <div className={styles.metaChip}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
              Dernière mise à jour : Janvier 2025
            </div>
            <div className={styles.metaChip}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Protection des données garantie
            </div>
          </div>
        </div>
        <svg className={styles.headerArc} viewBox="0 0 1440 48" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,48 C360,0 1080,0 1440,48 L1440,48 L0,48 Z" fill="var(--cream)" />
        </svg>
      </header>

      <div className={styles.layout}>
        <nav className={styles.toc} aria-label="Table des matières">
          <div className={styles.tocLabel}>Sommaire</div>
          <ul>
            {TOC_ITEMS.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={activeId === item.id ? styles.tocLinkActive : undefined}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <main className={styles.content}>
          <section className={styles.section} id="s1">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>01</div>
              <h2>Introduction</h2>
            </div>
            <p>
              La présente politique de confidentialité explique comment <strong>Dy'kass</strong> collecte, utilise et
              protège les données personnelles de ses utilisateurs, conformément au Règlement (UE) 2016/679 (RGPD).
            </p>
            <div className={styles.infoBox}>
              <span className={styles.infoIcon} aria-hidden>
                🛡️
              </span>
              <span>
                Nous nous engageons à traiter vos données avec le plus grand soin et dans le strict respect de la
                réglementation européenne en vigueur.
              </span>
            </div>
          </section>

          <section className={styles.section} id="s2">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>02</div>
              <h2>Responsable du traitement</h2>
            </div>
            <p>Le responsable du traitement des données est :</p>
            <div className={styles.highlight}>
              <strong>Dy'kass</strong>
              <br />
              Adresse : <strong>Akanda, Libreville Gabon</strong>
              <br />
              E-mail : <strong>bbndiad@gmail.com</strong>
            </div>
          </section>

          <section className={styles.section} id="s3">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>03</div>
              <h2>Données collectées</h2>
            </div>
            <p>Nous collectons uniquement les données nécessaires à la fourniture de nos services :</p>
            <ul className={styles.badgeList}>
              <li>
                <span className={styles.tag}>Identification</span> Nom, prénom, adresse e-mail, numéro de téléphone.
              </li>
              <li>
                <span className={styles.tag}>Livraison</span> Adresse postale, informations de paiement (traitées via
                prestataires sécurisés).
              </li>
              <li>
                <span className={styles.tag}>Techniques</span> Identifiant de l&apos;appareil, adresse IP, système
                d&apos;exploitation.
              </li>
              <li>
                <span className={styles.tag}>Utilisation</span> Historique de commandes, préférences, interactions avec
                l&apos;application.
              </li>
            </ul>
          </section>

          <section className={styles.section} id="s4">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>04</div>
              <h2>Finalités du traitement</h2>
            </div>
            <p>Vos données sont utilisées pour :</p>
            <ul className={styles.cardList}>
              <li>Gérer vos commandes et livraisons</li>
              <li>Fournir un service client efficace</li>
              <li>Personnaliser votre expérience utilisateur</li>
              <li>Envoyer des communications commerciales (si consenti)</li>
              <li>Assurer la sécurité et prévenir la fraude</li>
            </ul>
          </section>

          <section className={styles.section} id="s5">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>05</div>
              <h2>Base légale</h2>
            </div>
            <p>Le traitement de vos données repose sur :</p>
            <ul className={styles.badgeList}>
              <li>
                <span className={styles.tag}>Contrat</span> Exécution du contrat — gestion des commandes.
              </li>
              <li>
                <span className={styles.tag}>Consentement</span> Communications marketing.
              </li>
              <li>
                <span className={styles.tag}>Légal</span> Obligation légale — facturation, conservation.
              </li>
              <li>
                <span className={styles.tag}>Intérêt</span> Intérêt légitime — amélioration de nos services.
              </li>
            </ul>
          </section>

          <section className={styles.section} id="s6">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>06</div>
              <h2>Partage des données</h2>
            </div>
            <p>Vos données peuvent être transmises à :</p>
            <ul className={styles.pillList}>
              <li>Prestataires de paiement</li>
              <li>Prestataires de livraison</li>
              <li>Hébergeurs techniques</li>
              <li>Fournisseurs techniques</li>
              <li>Autorités compétentes</li>
            </ul>
            <div className={`${styles.infoBox} ${styles.infoBoxExtraTop}`}>
              <span className={styles.infoIcon} aria-hidden>
                🔒
              </span>
              <span>
                <strong>Engagement fort :</strong> Nous ne vendons jamais vos données personnelles à des tiers, quelles
                que soient les circonstances.
              </span>
            </div>
          </section>

          <section className={styles.section} id="s7">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>07</div>
              <h2>Conservation des données</h2>
            </div>
            <p>Les données sont conservées selon les règles suivantes :</p>
            <ul className={styles.badgeList}>
              <li>
                <span className={styles.tag}>Active</span> Pendant la durée nécessaire à l&apos;exécution du contrat.
              </li>
              <li>
                <span className={styles.tag}>Archive</span> Puis archivées selon les obligations légales (ex. : 5 ans pour
                les factures).
              </li>
              <li>
                <span className={styles.tag}>Fin</span> Supprimées ou anonymisées au-delà de ces délais.
              </li>
            </ul>
          </section>

          <section className={styles.section} id="s8">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>08</div>
              <h2>Droits des utilisateurs</h2>
            </div>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className={styles.cardList}>
              <li>Accès à vos données</li>
              <li>Rectification de vos informations</li>
              <li>Suppression (droit à l&apos;oubli)</li>
              <li>Opposition au traitement</li>
              <li>Portabilité de vos données</li>
            </ul>
            <div className={styles.contactCta}>
              <div>
                <strong>Exercer vos droits</strong>
                <p>Contactez-nous directement à l&apos;adresse dédiée à la protection des données.</p>
              </div>
              <a className={styles.ctaBtn} href="mailto:bbndiade@gmail.com">
                📧 bbndiade@gmail.com
              </a>
            </div>
          </section>

          <section className={styles.section} id="s9">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>09</div>
              <h2>Sécurité</h2>
            </div>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données
              contre tout accès non autorisé, toute perte ou toute divulgation accidentelle ou illicite.
            </p>
            <div className={styles.infoBox}>
              <span className={styles.infoIcon} aria-hidden>
                ⚙️
              </span>
              <span>
                Nos systèmes sont régulièrement audités et mis à jour afin de maintenir le plus haut niveau de sécurité
                possible pour vos informations personnelles.
              </span>
            </div>
          </section>

          <section className={styles.section} id="s10">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>10</div>
              <h2>Transferts hors UE</h2>
            </div>
            <p>
              Si des données sont transférées en dehors de l&apos;Union européenne, nous garantissons qu&apos;elles
              bénéficient d&apos;un niveau de protection adéquat.
            </p>
            <ul className={styles.pillList}>
              <li>Clauses contractuelles types</li>
              <li>Pays reconnus adéquats par la Commission européenne</li>
              <li>Mécanismes de protection certifiés</li>
            </ul>
          </section>

          <section className={styles.section} id="s11">
            <div className={styles.sectionHeader}>
              <div className={styles.sectionNum}>11</div>
              <h2>Modifications</h2>
            </div>
            <p>
              Nous pouvons mettre à jour cette politique de confidentialité à tout moment. Toute modification
              substantielle sera notifiée dans l&apos;application ou par e-mail à l&apos;adresse associée à votre compte.
            </p>
            <div className={styles.infoBox}>
              <span className={styles.infoIcon} aria-hidden>
                📢
              </span>
              <span>
                Nous vous encourageons à consulter régulièrement cette page pour rester informé des éventuelles mises à
                jour.
              </span>
            </div>
          </section>
        </main>
      </div>

      <footer className={styles.legalFooter}>
        <p>
          © 2025 <strong>Dykass</strong> — Tous droits réservés &nbsp;·&nbsp; Politique de confidentialité conforme au{' '}
          <strong>RGPD (UE) 2016/679</strong>
        </p>
      </footer>
    </div>
  );
}

export default PolitiqueConfidentialite;
